import {
  artToRows, bakeText, cloneArt, composeArt, fillArt, newArt, rasterizeText, rowsToArt, setSixel,
  type Art, type ArtSource, type PixelFontName, type TextBlock,
} from '@textbox/core';
import { ScreenRenderer, sizeScreen } from '@textbox/renderer';
import { COLOUR_HEX, el, select } from './ui.ts';

const COLOUR_NAMES = ['black', 'red', 'green', 'yellow', 'blue', 'magenta', 'cyan', 'white'];

/**
 * Block-art paint editor. The grid is at sixel resolution (2 × 3 per cell)
 * and sized to the target area. Pixels are painted directly; text blocks
 * are objects drawn in a pixel font that can be moved, restyled and
 * recoloured until they are baked into the pixels. Each cell has one
 * colour: painting a different colour into a cell recolours the whole
 * cell, exactly as a Level 1 set would show it. The preview underneath is
 * the exported rows through the real renderer, so anything lost to colour
 * attributes shows up as it will on air.
 */
export class ArtEditor {
  root: HTMLElement;
  source: ArtSource;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private colour = 3;
  private tool: 'paint' | 'erase' | 'select' = 'paint';
  private painting = false;
  private paintValue = true;
  private dragging: { id: string; dx: number; dy: number } | null = null;
  private selected: string | null = null;
  private previewEl: HTMLElement;
  private previewRenderer: ScreenRenderer;
  private status: HTMLElement;
  private textPanel: HTMLElement;
  private toolButtons: HTMLElement;
  private paletteEl: HTMLElement;
  private onChange: () => void;
  private sw = 14; // sixel width px
  private sh = 12; // sixel height px

  constructor(source: ArtSource, onChange: () => void) {
    this.source = source;
    this.onChange = onChange;
    this.canvas = el('canvas', { class: 'art-canvas', tabindex: '0' });
    this.ctx = this.canvas.getContext('2d')!;
    this.previewEl = el('div', { class: 'screen' });
    this.previewRenderer = new ScreenRenderer(this.previewEl);
    sizeScreen(this.previewEl, 10);
    this.status = el('div', { class: 'art-status' });
    this.textPanel = el('div', { class: 'text-panel' });

    this.paletteEl = el('div', { class: 'palette' }, COLOUR_HEX.map((hex, i) => el('button', {
      type: 'button', class: `swatch${i === this.colour ? ' on' : ''}`, style: { background: hex }, title: COLOUR_NAMES[i],
      onClick: () => this.setColour(i),
    })));
    this.toolButtons = el('div', { class: 'tools' },
      el('button', { type: 'button', class: 'on', dataset: { tool: 'paint' }, onClick: () => this.setTool('paint') }, 'Paint'),
      el('button', { type: 'button', dataset: { tool: 'erase' }, onClick: () => this.setTool('erase') }, 'Erase'),
      el('button', { type: 'button', dataset: { tool: 'select' }, onClick: () => this.setTool('select') }, 'Select text'),
      el('button', { type: 'button', onClick: () => { fillArt(this.source.art, this.colour); this.changed(); } }, 'Fill background'),
      el('button', { type: 'button', onClick: () => this.addText() }, 'Add text'),
      el('button', { type: 'button', onClick: () => { this.source.art = newArt(this.source.art.width, this.source.art.height); this.source.texts = []; this.selected = null; this.changed(); } }, 'Clear'),
    );
    this.root = el('div', { class: 'art-editor' },
      el('div', { class: 'art-bar' }, this.paletteEl, this.toolButtons),
      el('div', { class: 'art-scroll' }, this.canvas),
      this.textPanel,
      this.status,
      el('div', { class: 'art-preview-label' }, 'As broadcast (Level 1):'),
      this.previewEl,
    );

    this.canvas.addEventListener('mousedown', (e) => this.onDown(e));
    this.canvas.addEventListener('mousemove', (e) => this.onMove(e));
    window.addEventListener('mouseup', () => this.onUp());
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    this.canvas.addEventListener('keydown', (e) => this.onKey(e));
    this.redraw();
  }

  // ---- tools --------------------------------------------------------------
  private setColour(i: number): void {
    this.colour = i;
    this.paletteEl.querySelectorAll('.swatch').forEach((s, j) => s.classList.toggle('on', j === i));
    const t = this.selectedBlock();
    if (t && this.tool === 'select') { t.colour = i; t.cutout = false; this.changed(); }
    else if (this.tool === 'erase') this.setTool('paint');
  }

  private setTool(tool: 'paint' | 'erase' | 'select'): void {
    this.tool = tool;
    this.toolButtons.querySelectorAll('button').forEach((b) => b.classList.toggle('on', b.dataset['tool'] === tool));
    if (tool !== 'select') { this.selected = null; this.renderTextPanel(); this.redraw(); }
  }

  private addText(): void {
    const text = prompt('Text for the block (A–Z, 0–9, punctuation):', 'TITLE')?.trim();
    if (!text) return;
    const id = `t${Date.now().toString(36)}`;
    const filledUnder = this.source.art.cells.some((c) => c.bits === 0x3f);
    this.source.texts.push({ id, text, x: 2, y: 2, font: 'medium', colour: this.colour, cutout: filledUnder && this.colour === this.source.art.cells[0]?.colour });
    this.selected = id;
    this.setTool('select');
    this.selected = id;
    this.changed();
  }

  /** Warn when a block runs past the area (it is clipped on export). */
  private overflow(t: TextBlock): HTMLElement | null {
    const b = this.blockBounds(t);
    const over: string[] = [];
    if (b.x + b.w > this.source.art.width * 2) over.push(`${b.x + b.w - this.source.art.width * 2} sixels too wide`);
    if (b.y + b.h > this.source.art.height * 3) over.push(`${b.y + b.h - this.source.art.height * 3} sixels too tall`);
    if (b.x < 0 || b.y < 0) over.push('off the left/top edge');
    return over.length ? el('span', { class: 'error' }, ` clipped: ${over.join(', ')} — try a smaller size or shorter text`) : null;
  }

  private selectedBlock(): TextBlock | null {
    return this.source.texts.find((t) => t.id === this.selected) ?? null;
  }

  private blockBounds(t: TextBlock): { x: number; y: number; w: number; h: number } {
    const r = rasterizeText(t.text, t.font);
    return { x: t.x, y: t.y, w: r.width, h: r.height };
  }

  // ---- mouse ------------------------------------------------------------
  private sixelAt(e: MouseEvent): { sx: number; sy: number } {
    const r = this.canvas.getBoundingClientRect();
    return { sx: Math.floor((e.clientX - r.left) / this.sw), sy: Math.floor((e.clientY - r.top) / this.sh) };
  }

  private onDown(e: MouseEvent): void {
    e.preventDefault();
    this.canvas.focus();
    const { sx, sy } = this.sixelAt(e);
    if (this.tool === 'select') {
      const hit = [...this.source.texts].reverse().find((t) => { const b = this.blockBounds(t); return sx >= b.x && sx < b.x + b.w && sy >= b.y && sy < b.y + b.h; });
      this.selected = hit?.id ?? null;
      if (hit) this.dragging = { id: hit.id, dx: sx - hit.x, dy: sy - hit.y };
      this.renderTextPanel();
      this.redraw();
      return;
    }
    this.painting = true;
    this.paintValue = this.tool === 'paint' && e.button !== 2;
    this.paintAt(sx, sy);
  }

  private onMove(e: MouseEvent): void {
    const { sx, sy } = this.sixelAt(e);
    if (this.dragging) {
      const t = this.source.texts.find((x) => x.id === this.dragging!.id);
      if (t) { t.x = sx - this.dragging.dx; t.y = sy - this.dragging.dy; this.redraw(); }
      return;
    }
    if (this.painting) this.paintAt(sx, sy);
  }

  private onUp(): void {
    if (this.dragging) { this.dragging = null; this.changed(); }
    if (this.painting) { this.painting = false; this.onChange(); }
  }

  private onKey(e: KeyboardEvent): void {
    const t = this.selectedBlock();
    if (!t) return;
    const step = e.shiftKey ? 2 : 1;
    if (e.key === 'ArrowLeft') t.x -= step;
    else if (e.key === 'ArrowRight') t.x += step;
    else if (e.key === 'ArrowUp') t.y -= step;
    else if (e.key === 'ArrowDown') t.y += step;
    else if (e.key === 'Delete' || e.key === 'Backspace') { this.source.texts = this.source.texts.filter((x) => x.id !== t.id); this.selected = null; }
    else return;
    e.preventDefault();
    this.changed();
  }

  private paintAt(sx: number, sy: number): void {
    if (sx < 0 || sy < 0 || sx >= this.source.art.width * 2 || sy >= this.source.art.height * 3) return;
    setSixel(this.source.art, sx, sy, this.colour, this.paintValue);
    this.redraw();
  }

  private changed(): void {
    this.renderTextPanel();
    this.redraw();
    this.onChange();
  }

  // ---- text panel ---------------------------------------------------------
  private renderTextPanel(): void {
    const t = this.selectedBlock();
    if (!t) {
      this.textPanel.replaceChildren(this.source.texts.length
        ? el('span', { class: 'muted' }, `${this.source.texts.length} text block${this.source.texts.length > 1 ? 's' : ''} — choose "Select text" and click one to edit or drag it; arrow keys nudge.`)
        : el('span', { class: 'muted' }, '"Add text" places a pixel-font title you can drag into position; "Fill background" paints the whole area in the chosen colour.'));
      return;
    }
    const text = el('input', { type: 'text', value: t.text, onInput: () => { t.text = text.value; this.changed(); } });
    const font = select([{ value: 'small', label: 'Small (3×5)' }, { value: 'medium', label: 'Medium (5×7)' }, { value: 'wide', label: 'Wide (10×7)' }, { value: 'large', label: 'Large (10×14)' }], t.font, { onChange: () => { t.font = font.value as PixelFontName; this.changed(); } });
    const colour = select(COLOUR_NAMES.map((n, i) => ({ value: String(i), label: n })), String(t.colour), { onChange: () => { t.colour = Number(colour.value); t.cutout = false; cutout.checked = false; this.changed(); } });
    const cutout = el('input', { type: 'checkbox', checked: t.cutout, onChange: () => { t.cutout = cutout.checked; this.changed(); } });
    this.textPanel.replaceChildren(
      el('label', {}, 'Text ', text),
      el('label', {}, 'Size ', font),
      el('label', {}, 'Colour ', colour),
      el('label', { title: 'Clear the letters out of the background instead of painting them — costs no attribute cells on a filled band' }, cutout, ' cut out of background'),
      el('span', { class: 'muted' }, ` at ${t.x},${t.y}`),
      this.overflow(t) ?? '',
      el('button', { type: 'button', onClick: () => { this.source = bakeText(this.source, t.id); this.selected = null; this.changed(); } }, 'Bake into pixels'),
      el('button', { type: 'button', class: 'danger', onClick: () => { this.source.texts = this.source.texts.filter((x) => x.id !== t.id); this.selected = null; this.changed(); } }, 'Remove'),
    );
  }

  // ---- drawing ------------------------------------------------------------
  load(source: ArtSource): void {
    this.source = source;
    this.selected = null;
    this.changed();
  }

  resize(width: number, height: number): void {
    const next = newArt(width, height);
    const a = this.source.art;
    for (let y = 0; y < Math.min(height, a.height); y++) for (let x = 0; x < Math.min(width, a.width); x++) next.cells[y * width + x] = { ...a.cells[y * a.width + x]! };
    this.source.art = next;
    this.changed();
  }

  redraw(): void {
    const composed = composeArt(this.source);
    const { width, height } = composed;
    this.canvas.width = width * 2 * this.sw;
    this.canvas.height = height * 3 * this.sh;
    const ctx = this.ctx;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    const { rows, sacrificed } = artToRows(composed);
    const lost = new Set(sacrificed.map(([y, x]) => y * width + x));
    for (let cy = 0; cy < height; cy++) {
      for (let cx = 0; cx < width; cx++) {
        const cell = composed.cells[cy * width + cx]!;
        for (let b = 0; b < 6; b++) {
          const on = (cell.bits >> b) & 1;
          const sx = cx * 2 + (b % 2), sy = cy * 3 + Math.floor(b / 2);
          ctx.fillStyle = on ? COLOUR_HEX[cell.colour] ?? '#fff' : '#111';
          ctx.fillRect(sx * this.sw, sy * this.sh, this.sw - 1, this.sh - 1);
        }
        if (lost.has(cy * width + cx)) {
          ctx.strokeStyle = '#f0f';
          ctx.lineWidth = 2;
          ctx.strokeRect(cx * 2 * this.sw + 1, cy * 3 * this.sh + 1, 2 * this.sw - 3, 3 * this.sh - 3);
        }
      }
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    ctx.lineWidth = 1;
    for (let cx = 0; cx <= width; cx++) { ctx.beginPath(); ctx.moveTo(cx * 2 * this.sw - 0.5, 0); ctx.lineTo(cx * 2 * this.sw - 0.5, this.canvas.height); ctx.stroke(); }
    for (let cy = 0; cy <= height; cy++) { ctx.beginPath(); ctx.moveTo(0, cy * 3 * this.sh - 0.5); ctx.lineTo(this.canvas.width, cy * 3 * this.sh - 0.5); ctx.stroke(); }
    // text block outlines
    for (const t of this.source.texts) {
      const b = this.blockBounds(t);
      ctx.setLineDash(t.id === this.selected ? [] : [4, 3]);
      ctx.strokeStyle = t.id === this.selected ? '#ffd400' : 'rgba(255,255,255,0.6)';
      ctx.lineWidth = t.id === this.selected ? 2 : 1;
      ctx.strokeRect(b.x * this.sw - 1, b.y * this.sh - 1, b.w * this.sw + 1, b.h * this.sh + 1);
      ctx.setLineDash([]);
    }
    // preview: rows placed from row 1
    const grid = Array.from({ length: 25 }, () => new Array<number>(40).fill(0x20));
    rows.forEach((s, i) => { if (1 + i < 25) grid[1 + i] = Array.from(s, (ch) => ch.charCodeAt(0)); });
    this.previewRenderer.render(grid, { reveal: true, mix: false, links: [], hover: null, addressColour: 7 });
    const visible = sacrificed.filter(([, x]) => x > 1);
    this.status.textContent = visible.length
      ? `${visible.length} cell${visible.length > 1 ? 's' : ''} (outlined) lose pixels to colour changes — leave a blank cell before a new colour, or use a cut-out text block.`
      : 'Nothing is lost to colour attributes beyond the first two cells of each row.';
  }

  rows(): string[] {
    return artToRows(composeArt(this.source)).rows;
  }

  static fromAsset(rows: string[], width: number, source: ArtSource | null): ArtSource {
    if (source) return { art: cloneArt(source.art), texts: source.texts.map((t) => ({ ...t })) };
    return { art: rowsToArt(rows, width), texts: [] };
  }
}
